import React from 'react'
import EtContentEditorLight2 from "@/utils/EtContentEditorLight2"
import { useCreateNewUserReflectionMutation } from "@/features/userReflections/userReflectionsApiSlice"
import EtWabReflectionIcon1SVGComp from "@/assets/img/icons/svgComp/EtWabReflectionIcon1SVGComp"

//05 Dec 2025
import { calculateWeekBoundaries } from "@/utils/dateUtils"

const EtCreateUserReflectionComp = ({
  userId,
  userOrgId,
  onClose,
  refetchUserReflections,
  refetchUserReflectionsAll,
  defaultTrailId,
  defaultSessionId,

  // 31 Aug 2026
  canSyncGoogleDoc = false,
}) => {

  // First Created: 22 Sep 2024  
  // Author: Atul Pandey
  // Updated - 05 Dec 2025 - Built the complete logic to create a user reflection note inside a UserReflection record and cascade to UserReflectionAll
  // Updated - 31 Aug 2026 - added canSyncGoogleDoc  

  // ---------------------------------------------
  // 1️⃣ Local State
  // ---------------------------------------------
  const [editorValue, setEditorValue] = React.useState("");
  const [markAsMagicMoment, setMarkAsMagicMoment] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  const [editorKey, setEditorKey] = React.useState(
      "reflection-create-editor-" + Date.now().toString()
    );
  
  // 31 Aug 2026 - Per-save Google Docs sync choice
  const [
    syncGoogleDoc,
    setSyncGoogleDoc,
  ] = React.useState(
    canSyncGoogleDoc
  );

  // Keep sync disabled if Google Docs becomes unavailable
  React.useEffect(() => {

    if (
      !canSyncGoogleDoc
    ) {

      setSyncGoogleDoc(
        false
      );

    }

  }, [
    canSyncGoogleDoc,
  ]);

  // ---------------------------------------------
  // 2️⃣ RTK Mutation Hook
  // ---------------------------------------------
  const [createNewUserReflection] = useCreateNewUserReflectionMutation();


  // ---------------------------------------------
  // 3️⃣ Calculate Week Boundaries for NEW Note
  // ---------------------------------------------
  const today = new Date();
  // const { weekStarting: currentWeekStarting, weekEnding: currentWeekEnding } = calculateWeekBoundaries(today);
  const { weekStarting: currentWeekStarting } = calculateWeekBoundaries(today);


  // ---------------------------------------------
  // 4️⃣ Save Handler
  // ---------------------------------------------
  const handleSave = async () => {
    if (!editorValue || editorValue.trim() === "") {
      alert("Reflection cannot be empty.");
      return;
    }

    try {
      setIsSaving(true);

      // ---- Build NEW reflection note ----
      const newNote = {
        userReflectionNoteText: editorValue,
        userReflectionNoteDate: new Date(),
        userReflectionNoteUpdateDate: new Date(),
        markAsMagicMoment: markAsMagicMoment,
      };

      // ---- FINAL PAYLOAD ----
      const payload = {
        userId,
        userOrgId,
        weekStarting: currentWeekStarting,
        userReflectionNotes: [newNote],
        trailId: defaultTrailId || null,
        sessionId: defaultSessionId || null,

        // 31 Aug 2026
        // Transient request instruction only
        syncGoogleDoc:
          canSyncGoogleDoc &&
          syncGoogleDoc,
      };

      // console.log("🔶 CREATE Reflection Payload:", payload);

      await createNewUserReflection(payload).unwrap();

      // Refresh parent view
      refetchUserReflectionsAll?.();
      refetchUserReflections?.();

      onClose();

    } catch (err) {
      console.error("❌ Failed to create reflection:", err);
      alert("Something went wrong while saving your reflection.");
    } finally {
      setIsSaving(false);
    }
  };


  // ---------------------------------------------
  // 5️⃣ UI
  // ---------------------------------------------
  return (
    <div className="p-4 bg-white rounded font-poppins">

      {/* Header */}
      <div className="flex items-center mb-4">
        <EtWabReflectionIcon1SVGComp className="h-7 w-7 text-orange-600" />
        <p className="text-lg font-bold text-gray-800 ml-2">Add New Reflection</p>
      </div>

      {/* Editor */}
      <EtContentEditorLight2
        value={editorValue}
        handleChange={(v) => setEditorValue(v)}
        placeholderText="Write your reflection..."
        editorKey={editorKey}
      />

      {/* Magic Moment Toggle */}
      <div className="flex items-center my-3">
        <input
          type="checkbox"
          className="mr-2"
          checked={markAsMagicMoment}
          onChange={(e) => setMarkAsMagicMoment(e.target.checked)}
        />
        <label className="text-xs text-gray-600">
          Mark as a Magic Moment
        </label>
      </div>

      {/* ------------------------------------------- */}
      {/* 31 Aug 2026 - Google Docs Sync              */}
      {/* ------------------------------------------- */}

      {canSyncGoogleDoc && (

        <div
          className="
            flex
            items-center
            my-3
            px-3
            py-2
            bg-gray-50
            border
            border-gray-200
            rounded
          "
        >

          <input
            type="checkbox"
            checked={syncGoogleDoc}
            onChange={
              e =>
                setSyncGoogleDoc(
                  e.target.checked
                )
            }
            className="mr-2"
          />

          <div>

            <label className="text-xs font-semibold text-gray-700">
              Sync this reflection to Google Docs
            </label>

            <p className="text-[10px] text-gray-500">
              Save this reflection to your connected Google Docs.
            </p>

          </div>

        </div>

      )}

      {/* Buttons */}
      <div className="flex space-x-3 mt-4">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-4 py-2 bg-lime-600 text-white rounded-full text-sm"
        >
          {isSaving ? "Saving..." : "Save"}
        </button>

        <button
          onClick={onClose}
          className="px-4 py-2 bg-orange-600 text-white rounded-full text-sm"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default EtCreateUserReflectionComp;